import axios from 'axios'
import {API_ROOT_URL} from 'envs/_current/config'
import {clearDeviceSession, getDeviceId, getSessionId, getSessionSecret, setDeviceSession,} from 'helpers/localStorage'

// ─── HMAC-SHA256 qua WebCrypto — không thêm package, xem docs/api-security-design.md mục 3/13 ──
async function hmacSha256Hex(secret, message) {
  const encoder = new TextEncoder()
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    {name: 'HMAC', hash: 'SHA-256'},
    false,
    ['sign']
  )
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(message))
  return bufferToHex(signature)
}

async function sha256Hex(message) {
  const encoder = new TextEncoder()
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(message))
  return bufferToHex(digest)
}

function bufferToHex(buffer) {
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

function bodyStringForSigning(data) {
  // FormData (upload file) — không hash raw body, khớp với backend (xem VerifySignature.php)
  if (typeof FormData !== 'undefined' && data instanceof FormData) return ''
  if (data === undefined || data === null) return ''
  if (typeof data === 'string') return data
  return JSON.stringify(data)
}

// ─── Ký 1 request — trả về headers cần gắn thêm ───────────────────────────────
export async function signRequest(method, fullUrl, data) {
  const sessionSecret = getSessionSecret()
  const sessionId = getSessionId()
  if (!sessionSecret || !sessionId) {
    return null
  }

  const url = new URL(fullUrl, window.location.origin)
  const pathAndQuery = url.pathname + (url.search || '')

  const timestamp = Math.floor(Date.now() / 1000)
  const nonce = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36)

  const bodyHash = await sha256Hex(bodyStringForSigning(data))
  const signingString = [
    method.toUpperCase(),
    pathAndQuery,
    String(timestamp),
    nonce,
    bodyHash,
  ].join('\n')

  const signature = await hmacSha256Hex(sessionSecret, signingString)

  return {
    'X-Session-Id': sessionId,
    'X-Timestamp': String(timestamp),
    'X-Nonce': nonce,
    'X-Signature': signature,
  }
}

// ─── Bootstrap session — gọi lúc app boot hoặc khi nhận 419 (device_session_expired) ──
// Dùng axios trực tiếp (không qua callAPI) để tránh vòng lặp import, vì endpoint này
// không cần/không thể ký (chưa có secret) — xem docs/api-security-design.md mục 7/14.
let pendingInit = null

export function initDeviceSession() {
  if (pendingInit) return pendingInit

  pendingInit = axios
    .post(`${API_ROOT_URL}/session/init`, null, {
      headers: {'X-Device-Id': getDeviceId()},
    })
    .then(response => {
      const {session_id, session_secret} = response.data || {}
      if (session_id && session_secret) {
        setDeviceSession(session_id, session_secret)
      }
      return response.data
    })
    .catch(() => null)
    .finally(() => {
      pendingInit = null
    })

  return pendingInit
}

export function hasDeviceSession() {
  return !!(getSessionId() && getSessionSecret())
}

export function applyRotatedSecretFromResponse(response) {
  const newSecret = response && response.headers && response.headers['x-new-session-secret']
  if (newSecret) {
    setDeviceSession(getSessionId(), newSecret)
  }
}

export {clearDeviceSession}
